import { useState } from 'react';
import { InlineLoading } from '@carbon/react';
import { useAgentRunPrompt } from '../hooks';
import style from './audit-log.module.scss';

interface RunPromptProps {
  /** The run's prompt endpoint; loaded only once the section is opened. */
  href: string;
}

/**
 * A run's prompt, read when its section opens rather than carried on every feed row. Shown as
 * plain text: it holds document text, which must not render as markdown (an image link in an
 * extracted name would load from its host).
 *
 * @param {RunPromptProps} props - The run's prompt endpoint.
 * @return {JSX.Element} The collapsible Prompt section.
 */
export function RunPrompt({ href }: RunPromptProps) {
  const [open, setOpen] = useState(false);
  const { data, isPending, isFetching, isError } = useAgentRunPrompt(
    href,
    open
  );

  let body = <pre className={style.rawBlock}>{data?.prompt || '—'}</pre>;
  if (isError && !isFetching) {
    body = (
      <div className={style.rawBlockBody} role="alert">
        Couldn&apos;t load the prompt.
      </div>
    );
  } else if (isPending) {
    body = (
      <div className={style.rawBlockBody}>
        <InlineLoading description="Loading prompt" />
      </div>
    );
  }

  return (
    <details
      className={style.rawBlockWrapper}
      onToggle={(event) => setOpen(event.currentTarget.open)}
    >
      <summary className={style.rawBlockSummary}>Prompt</summary>
      {open && body}
    </details>
  );
}
